import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ClearButton from './ClearButton';
import Tooltip from '@homeaway/react-tooltip';
import Modal from '@homeaway/react-modal';
import {ButtonLink, ButtonPrimary} from '@homeaway/react-buttons';
import NavCalendar from '../NavCalendar/NavCalendar';
import propTypeDateString from '../utils/propTypeDateString';
import './DatePickerBase.less';

const checkElementType = (props, propName, componentName) => {
    const instance =
        typeof window !== 'undefined' && window.Element
            ? window.Element
            : Object;
    if (props[propName] && !(props[propName] instanceof instance)) {
        return new Error(
            `Invalid prop '${propName}' supplied to '${componentName}'. Validation failed.`
        );
    }
    return null;
};

class DatePickerBase extends Component {
    static propTypes = {
        alertTemplateComponent: PropTypes.func,
        alertTemplateData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** Optional labels object to override default locale-driven labels on calendar */
        calendarLabels: PropTypes.shape({
            /** For locales that need a year label while displaying date e.g. ja-JP*/
            dayNames: PropTypes.arrayOf(PropTypes.string),
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            yearLabel: PropTypes.string
        }),
        calendarRefFunc: PropTypes.func,
        centered: PropTypes.bool,
        className: PropTypes.string,
        clearButtonLabel: PropTypes.string,
        closeButtonLabel: PropTypes.string,
        closeOnOutsideClick: PropTypes.bool,
        continueButtonLabel: PropTypes.string,
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        dayTemplateComponent: PropTypes.func,
        /** Flag that specifies whether year should be displayed first, useful in locales like ja-JP*/
        displayYearFirst: PropTypes.bool,
        endDate: propTypeDateString,
        /** Flag to determine if there is a fade transition when the tooltip closes or not. True by default. */
        fadeOut: PropTypes.bool,
        inputs: PropTypes.element,
        isInline: PropTypes.bool,
        isMobile: PropTypes.bool,
        isOpen: PropTypes.bool,
        /** Object containing aria label translations for the current locale. Required for a11y. */
        i18n: PropTypes.shape({
            /** It is assumed that the parent app will prepare and provide the i18n object matching the shape seen here. */
            ariaLabelDatesCleared: PropTypes.string,
            ariaLabelCheckinCleared: PropTypes.string,
            ariaLabelCheckinSelected: PropTypes.string,
            ariaLabelCheckoutCleared: PropTypes.string,
            ariaLabelCheckoutSelected: PropTypes.string,
            ariaLabelClose: PropTypes.string,
            ariaLabelNextButton: PropTypes.string,
            ariaLabelNotValidCheckin: PropTypes.string,
            ariaLabelNotValidCheckout: PropTypes.string,
            ariaLabelPrevButton: PropTypes.string,
            ariaLabelUnavailable: PropTypes.string,
            /** The aria label used for screen readers to describe how to use the date picker. */
            directions: PropTypes.string
        }),
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        minDate: PropTypes.instanceOf(Date),
        maxDate: PropTypes.instanceOf(Date),
        /** There are three different mobile behaviors (2 are only for use with the affiliates program), 1) scrolling - switches to a vertical scrolling list. 2) single-affiliates - special single month mode for affiliates program (fits within 392px height). 3) double-affiliates - special double month mode for affiliates program (fits within 392px height). */
        mobileBehavior: PropTypes.oneOf([
            'scrolling',
            'single-affiliates',
            'double-affiliates'
        ]),
        modalTitle: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
        numMonthsToDisplay: PropTypes.number,
        onCancel: PropTypes.func,
        /** Function to call when a date picker is closed */
        onClose: PropTypes.func,
        onDatesCleared: PropTypes.func,
        onFinishClose: PropTypes.func,
        /** Function to call when the date picker opens */
        onOpen: PropTypes.func,
        onSelectionChanged: PropTypes.func,
        onSubmit: PropTypes.func,
        parentNode: checkElementType,
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        scrollNode: checkElementType,
        selectionChangeMode: PropTypes.string,
        selectionType: PropTypes.string,
        showContinueButton: PropTypes.bool,
        startDate: propTypeDateString,
        year: PropTypes.number
    };

    static defaultProps = {
        calendarLabels: {
            yearLabel: ''
        },
        centered: true,
        className: '',
        clearButtonLabel: 'Clear dates',
        closeButtonLabel: 'Close',
        closeOnOutsideClick: true,
        continueButtonLabel: 'Continue',
        displayYearFirst: false,
        isDelayed: false,
        isMobile: true,
        isOpen: false,
        i18n: {},
        locale: 'en_US',
        mobileBehavior: 'scrolling',
        modalTitle: null,
        numMonthsToDisplay: 2,
        onCancel: () => {},
        onClose: () => {},
        onFinishClose: () => {},
        onFocus: () => {},
        onOpen: () => {},
        onSubmit: () => {},
        placement: 'bottom',
        selectionChangeMode: 'start',
        selectionType: 'range',
        showContinueButton: false
    };

    static checkElementType = checkElementType;

    constructor(props) {
        super(props);

        this.tooltip = null;
        this.modal = null;
    }

    disableClearButton(startDate, endDate, date) {
        return !startDate && !endDate && !date;
    }

    handleKeyDownClear = (e) => {
        const {isMobile, onKeydownDatesCleared} = this.props;

        if (!isMobile) {
            onKeydownDatesCleared(e, this.tooltip);
        } else {
            onKeydownDatesCleared(e, this.modal);
        }
    };

    handleOnClose = () => {
        this.props.onClose(this.props.onCancel);
    };

    handleOnContinue = () => {
        if (this.props.isInline) {
            this.props.onSubmit();
        }

        this.props.onClose(this.props.onSubmit);
    };

    handleTooltipClose = () => {
        this.handleOnContinue();
        this.props.onFinishClose();
    };

    handleOnKeyDown = (e) => {
        // Prevent page from scrolling when using Arrow Up and Arrow Down keys navigation
        if (e.which === 38 || e.which === 40) {
            e.preventDefault();
        }
    };

    updateFocusable = () => {
        if (this.tooltip !== null && !this.props.isMobile) {
            this.tooltip.queryFocusableElements();
        }
    };

    renderAlertTemplateComponent() {
        const {alertTemplateComponent, alertTemplateData} = this.props;
        const AlertTemplateComponent = alertTemplateComponent;

        if (alertTemplateComponent) {
            return (
                <div className="picker__alert">
                    <AlertTemplateComponent data={alertTemplateData} />
                </div>
            );
        }

        return null;
    }

    renderCalendar(props) {
        if (props.isDelayed) {
            return false;
        }
        /* eslint-disable no-unused-vars */
        const {
            alertTemplateComponent,
            alertTemplateData,
            calendarLabels,
            className,
            clearButtonLabel,
            closeButtonLabel,
            continueButtonLabel,
            date,
            displayYearFirst,
            endDate,
            isInline,
            isMobile,
            i18n,
            locale,
            maxDate,
            minDate,
            mobileBehavior,
            month,
            numMonthsToDisplay,
            onSelectionChanged,
            selectionChangeMode,
            selectionType,
            showContinueButton,
            startDate,
            year,
            ...rest
        } = props;
        /* eslint-enable no-unused-vars */

        const clearButtonClasses = classNames('picker__clear', {
            mobile: isMobile
        });
        const closeButtonClasses = classNames('picker__close-btn', {
            mobile: isMobile
        });
        const showAlert = alertTemplateComponent ? true : false;
        const disableClearButton = this.disableClearButton(
            startDate,
            endDate,
            date
        );
        const closeButtonContainerClasses = classNames(
            'picker__close-container text-right',
            {
                daterange: selectionType === 'range'
            }
        );
        const continueBtnWrapperClasses = classNames('picker__continue', {
            'picker__continue--inline': isInline
        });

        const navCalendarClasses = classNames('picker__calendar', {
            [selectionType]: true,
            'picker__calendar--mobile-paging-single-affiliates':
                isMobile && mobileBehavior === 'single-affiliates',
            'picker__calendar--mobile-paging-double-affiliates':
                isMobile && mobileBehavior === 'double-affiliates',
            'picker__calendar--inline': isInline
        });

        const needsTooltipButtons = !(isMobile || isInline);

        return (
            <div
                className="datepicker"
                ref={props.calendarRefFunc}
                onKeyDown={this.handleOnKeyDown}
            >
                {needsTooltipButtons && (
                    <div className={closeButtonContainerClasses}>
                        <ButtonLink
                            className={closeButtonClasses}
                            label={closeButtonLabel}
                            onClick={this.handleTooltipClose}
                        >
                            <span className="sr-only">
                                {i18n.ariaLabelClose}
                            </span>
                        </ButtonLink>
                    </div>
                )}
                <NavCalendar
                    className={navCalendarClasses}
                    displayYearFirst={displayYearFirst}
                    isInline={isInline}
                    isMobile={isMobile}
                    i18n={i18n}
                    labels={calendarLabels}
                    locale={locale}
                    minDate={minDate}
                    maxDate={maxDate}
                    mobileBehavior={mobileBehavior}
                    month={month}
                    numMonthsToDisplay={numMonthsToDisplay}
                    onSelectionChanged={onSelectionChanged}
                    onUpdateFocusable={this.updateFocusable} //eslint-disable-line
                    scrollToMonth={isMobile && mobileBehavior === 'scrolling'}
                    selectable
                    selectedDate={date}
                    selectionEnd={endDate}
                    selectionChangeMode={selectionChangeMode}
                    selectionStart={startDate}
                    selectionType={selectionType}
                    year={year}
                    {...rest}
                />

                {!needsTooltipButtons && showContinueButton && (
                    <div className={continueBtnWrapperClasses}>
                        <ButtonPrimary
                            id="picker-continue-button"
                            block={!isInline}
                            label={continueButtonLabel}
                            onClick={this.handleOnContinue}
                        />
                    </div>
                )}
                {needsTooltipButtons && (
                    <ClearButton
                        className={clearButtonClasses}
                        disabled={disableClearButton}
                        handleClick={props.onDatesCleared}
                        handleKeyDown={this.handleKeyDownClear}
                        label={clearButtonLabel}
                    />
                )}
                {showAlert && this.renderAlertTemplateComponent()}
            </div>
        );
    }

    render() {
        const {
            centered,
            className,
            closeOnOutsideClick,
            inputs,
            isInline,
            isMobile,
            isOpen,
            onFocus,
            onOpen,
            parentNode,
            placement,
            scrollNode,
            fadeOut,
            modalTitle,
            clearButtonLabel,
            onDatesCleared
        } = this.props;

        // Inline (calendar wrapped in component; e.g. sidebar)
        if (isInline) {
            return (
                <div className="picker-inline-wrapper">
                    <div className={className} style={this.props.style}>
                        {this.renderCalendar(this.props)}
                    </div>
                </div>
            );
        }

        // Mobile (calendar in modal)
        if (isMobile) {
            return (
                <div className={className}>
                    {inputs}
                    <Modal
                        ref={(c) => {
                            this.modal = c;
                        }}
                        id="picker-modal"
                        className="picker__modal"
                        title={modalTitle}
                        isOpen={isOpen}
                        onClose={this.handleOnClose}
                        onModalClose={this.props.onFinishClose}
                        onModalOpen={onOpen}
                        isWorkflow
                        secondaryActionLabel={clearButtonLabel}
                        secondaryActionDisabled={this.disableClearButton(
                            this.props.startDate,
                            this.props.endDate,
                            this.props.date
                        )}
                        secondaryActionCallback={onDatesCleared}
                    >
                        {this.renderCalendar(this.props)}
                    </Modal>
                </div>
            );
        }

        // Desktop (calendar in popover)
        return (
            <div className={className}>
                <Tooltip
                    ref={(c) => {
                        this.tooltip = c;
                    }}
                    fadeOut={fadeOut}
                    content={this.renderCalendar(this.props)}
                    isOpen={isOpen}
                    onFocus={onFocus}
                    onClose={this.handleTooltipClose}
                    onOpen={onOpen}
                    parentNode={parentNode}
                    placement={placement}
                    portalClassName="picker__popover"
                    scrollNode={scrollNode}
                    tooltipType="popover"
                    trigger="none"
                    wrappingMethod="block"
                    closeOnOutsideClick={
                        closeOnOutsideClick ||
                        !(this.props.startDate && !this.props.endDate)
                    }
                    centered={centered}
                    interactiveContent
                    interactiveContainer
                >
                    {inputs}
                </Tooltip>
            </div>
        );
    }
}

export default DatePickerBase;
